import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "@/hooks/use-toast";
import { Mail, Loader2, MessageCircle, ArrowLeft } from "lucide-react";

const WHATSAPP = "5573981988263";

const ConfirmarEmailPage: React.FC = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleResend = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      toast({ title: "E-mail inválido", description: "Digite um e-mail válido.", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      const { error } = await supabase.auth.resend({
        type: "signup",
        email: trimmed,
        options: { emailRedirectTo: `${window.location.origin}/auth/callback` },
      });
      if (error) throw error;
      toast({
        title: "E-mail reenviado!",
        description: "Verifique sua caixa de entrada e também a pasta de SPAM.",
      });
    } catch (err: any) {
      toast({
        title: "Não foi possível reenviar",
        description: err?.message || "Tente novamente ou fale com o suporte.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const openWhatsApp = () => {
    const msg = `Olá! Preciso de ajuda para confirmar meu e-mail no IA Palpite Guru. Meu e-mail é: ${email || "(informar)"}`;
    window.open(`https://wa.me/${WHATSAPP}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-primary/5 px-4 py-8">
      <Card className="w-full max-w-md shadow-xl">
        <CardHeader className="text-center">
          <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-3">
            <Mail className="w-8 h-8 text-blue-600" />
          </div>
          <CardTitle className="text-2xl">Confirmar E-mail</CardTitle>
          <CardDescription>
            Não recebeu o e-mail de confirmação? Reenviamos agora mesmo.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <form onSubmit={handleResend} className="space-y-3">
            <div className="space-y-2">
              <Label htmlFor="email">Seu e-mail de cadastro</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="seu@email.com"
                required
                disabled={loading}
              />
            </div>
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Reenviando...</>
              ) : (
                "Reenviar e-mail de confirmação"
              )}
            </Button>
          </form>

          <div className="rounded-lg bg-yellow-50 border border-yellow-200 p-3 text-sm text-yellow-800 space-y-1">
            <p className="font-semibold">⚠️ Não está achando?</p>
            <p>• Verifique a pasta de <strong>SPAM</strong> ou Lixo Eletrônico</p>
            <p>• Procure pelo título <strong>"IA PALPITE GURU, IMPORTANTE: Confirme seu Email!"</strong></p>
            <p>• Remetente pode aparecer como <strong>Supabase Auth</strong></p>
          </div>

          <Button variant="outline" className="w-full" onClick={openWhatsApp}>
            <MessageCircle className="mr-2 h-4 w-4" /> Falar com suporte no WhatsApp
          </Button>

          <div className="flex items-center justify-between pt-2 text-sm">
            <button
              onClick={() => navigate("/login")}
              className="text-primary hover:underline flex items-center gap-1"
            >
              <ArrowLeft className="w-3 h-3" /> Voltar ao login
            </button>
            <Link to="/cadastro" className="text-muted-foreground hover:underline">
              Criar nova conta
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ConfirmarEmailPage;
